// Take a customer order (M7, WP7.2): see orders/form.tsx.

import { canOpenArea } from '@enp/domain/permissions';
import { PageHeader, toast } from '@enp/ui';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { OrderForm } from '../../../orders/form.js';
import { orderQuery } from '../../../orders/queries.js';
import { OrdersBreadcrumb } from '../../../orders/parts.js';
import { invalidatePlanning } from '../../../planning/queries.js';
import { NoPermission } from '../../../purchasing/parts.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { accountToday } from '../../../stock/labels.js';
import { locationsQuery } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/orders/new')({
  component: NewOrderPage,
});

function NewOrderPage() {
  useDocumentTitle('New order');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const locations = useQuery({
    ...locationsQuery(),
    enabled: canOpenArea(me.permissions, 'stock'),
  });
  const branches = useMemo(() => {
    const seen = new Map<string, string>();
    for (const l of locations.data?.items ?? []) seen.set(l.branch.id, l.branch.name);
    return [...seen]
      .map(([value, label]) => ({ value, label }))
      .filter((b) => me.branch_scope === null || b.value === me.branch_scope);
  }, [locations.data, me.branch_scope]);
  const canEdit = me.permissions.includes('orders.edit');
  return (
    <>
      <PageHeader
        eyebrow={<OrdersBreadcrumb page="New order" />}
        title="New order"
        description="A custom cake, a wholesale order or anything promised for later. Planning checks it as soon as it’s taken."
      />
      {canEdit ? (
        <OrderForm
          timeZone={me.account.timezone}
          today={accountToday(me.account)}
          branches={branches}
          defaultBranch={me.branch_scope ?? undefined}
          onCancel={() => void navigate({ to: '/orders' })}
          onSaved={async (o, how) => {
            queryClient.setQueryData(orderQuery(o.id).queryKey, o);
            await invalidatePlanning(queryClient);
            toast.success(how === 'draft' ? `${o.number} saved as a draft` : `${o.number} taken`, {
              description:
                how === 'draft'
                  ? 'A draft isn’t a commitment: planning checks it once you confirm it.'
                  : 'Planning checks it now: see its coverage on the order.',
            });
            void navigate({ to: '/orders/$orderId', params: { orderId: o.id } });
          }}
        />
      ) : (
        <NoPermission>
          Taking orders needs the “take and edit orders” permission. Ask the owner to change your
          role.
        </NoPermission>
      )}
    </>
  );
}
